import {
  Box,
  Button,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { Link, useParams } from 'react-router-dom';

import {
  cancelConfirmation,
  confirmationInitial,
  fixConfirmation,
  getConfirmationById,
  getConfirmationReinit,
  getSupersetToken,
} from '../../../../packages/common/api';
import Page from '../../../../packages/common/shared-ui/src/components/Page';
import { useTranslation } from '../../../../packages/common/utils/getLocalString';
import { generateRedirectUrl } from '../../../../packages/common/utils/getRedirectUrl';

import ConfirmationDocumentsTable from './components/ConfirmationDocumentsTable/ConfirmationDocumentsTable';
import ConfirmationResultTable from './components/ConfirmationResultTable/ConfirmationResultTable';

// Original class: blockWrapper
const StyledBlockWrapper = styled(Grid)(theme => ({
  width: '100%',
  padding: '24px 32px',
  borderRadius: '16px',
  backgroundColor: '#fff',
}));
// Original class: issueLink
const withIssueLinkStyles = Component => {
  return styled(Component)(({ theme }) => ({
    '&:hover': {
      textDecoration: 'underline',
    },
  }));
};
export const StyledIssueLink = withIssueLinkStyles(Link);
export const StyledIssueLinkBox = withIssueLinkStyles(Box);
const ConfirmationPage = () => {
  const getLocalString = useTranslation();
  const PageState = { VIEW: 'view', REINIT: 'reinit' };
  const [pageTitle, setPageTitle] = useState('');
  const [loading, setLoading] = useState(true);
  const [generalConfirmation, setGeneralConfirmation] = useState([]);
  const [confirmationResult, setConfirmationResult] = useState({
    columns: [],
    rows: [],
  });
  const [confirmationDocuments, setConfirmationDocuments] = useState({
    columns: [],
    rows: [],
  });
  const [confirmationPageState, setConfirmationPageState] = useState(null);
  const [confirmationCancel, setConfirmationCancel] = useState(false);
  const [confirmationCanceled, setConfirmationCanceled] = useState(false);
  const [confirmationFix, setConfirmationFix] = useState(false);
  const pageParams = useParams();
  const location = useLocation();
  const pageState = useRef(
    new Map([
      [PageState.VIEW, { getResponse: getConfirmationById }],
      [PageState.REINIT, { getResponse: getConfirmationReinit }],
    ]),
  );
  const urlParams = useRef(new URLSearchParams(location.search));
  const navigate = useNavigate();
  const handleError = useCallback(
    error => {
      if (error) {
        navigate(`/errors/error-${error.status}`);
      }
    },
    [navigate],
  );

  useEffect(() => {
    pageParams && pageParams.id && setConfirmationPageState(PageState.VIEW);
    location.pathname.includes(PageState.REINIT) &&
      setConfirmationPageState(PageState.REINIT);
  }, [PageState.REINIT, PageState.VIEW, location.pathname, pageParams]);

  useEffect(() => {
    let ignore = false;
    async function getConfirmation() {
      let params;
      confirmationPageState === PageState.VIEW &&
        pageParams &&
        (params = pageParams.id);
      if (
        confirmationPageState === PageState.REINIT &&
        urlParams.current.get('issues')
      ) {
        params = {};
        params.issues = urlParams.current.get('issues').replaceAll(',', '||');
      }
      const { data: confirmation } = await pageState.current
        .get(confirmationPageState)
        .getResponse(params);
      if (!ignore) {
        return confirmation;
      }
    }
    if (confirmationPageState) {
      getConfirmation()
        .then(res => {
          res.subject && setPageTitle(res.subject);
          res.standards && setGeneralConfirmation(res.standards);
          if (res.documents) {
            const rowsValue = res.documents.rows.filter(
              item => item.rowId !== 'total',
            );
            res.documents.fields &&
              res.documents.rows &&
              setConfirmationDocuments({
                columns: res.documents.fields,
                rows: rowsValue,
              });
          }
          if (res.results) {
            const rowsValue = res.results.rows.filter(
              item => item.rowId !== 'total',
            );
            res.results.fields &&
              res.results.rows &&
              setConfirmationResult({
                columns: res.results.fields,
                rows: rowsValue,
              });
          }
          res.canCancel && setConfirmationCancel(true);
          res.canFix && setConfirmationFix(true);
          setLoading(false);
        })
        .catch(error => {
          handleError(error.response);
        });
      return () => {
        ignore = true;
      };
    }
  }, [
    PageState.VIEW,
    PageState.REINIT,
    confirmationPageState,
    handleError,
    pageParams,
    pageParams.id,
  ]);

  const handleCancelConfirmation = useCallback(async () => {
    try {
      const { data: response } = await cancelConfirmation(pageParams.id);
      if (response) {
        setConfirmationCancel(false);
        setConfirmationCanceled(true);
        navigate(`/confirmations/${Number(response)}`);
      }
    } catch (error) {
      console.error('ERROR WITH POST', error);
    }
  }, [navigate, pageParams.id]);

  const handleFixConfirmation = useCallback(async () => {
    try {
      const { data: response } = await fixConfirmation(pageParams.id);
      if (response) {
        setConfirmationFix(false);
        navigate(`/confirmations/${Number(response)}`);
      }
    } catch (error) {
      console.error('ERROR WITH POST', error);
    }
  }, [navigate, pageParams.id]);
  const handleGetRedirectUrl = useCallback((id, columnType) => {
    switch (columnType) {
      case 'documents':
        return `${window.location.origin}/issues/${id}`;
      case 'project':
        return `${window.location.origin}/projects/${id}`;
      default:
        return '';
    }
  }, []);

  const handleReportLink = useCallback(async link => {
    window.open(`${link}`, '_blank');
  }, []);
  return (
    <>
      {loading ? (
        <Grid
          container
          sx={{
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '70px',
          }}
        >
          <CircularProgress color='secondary' />
        </Grid>
      ) : (
        <Page title={pageTitle} width='100%' sx={{ paddingBottom: '32px' }}>
          <Grid
            container
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography
                variant='h1'
                sx={{
                  m: 0,
                  padding: '10px 0',
                }}
              >
                {pageTitle}
              </Typography>
            </Grid>
            {confirmationCancel && (
              <Grid>
                <Button
                  variant='defaultGreyPrimary'
                  disableRipple
                  sx={{ marginLeft: '8px' }}
                  onClick={handleCancelConfirmation}
                >
                  <Typography
                    variant='h4'
                    sx={{
                      fontWeight: 700,
                    }}
                  >
                    {getLocalString('buttons', 'cancel')}
                  </Typography>
                </Button>
              </Grid>
            )}
            {confirmationFix && (
              <Grid>
                <Button
                  variant='defaultBlueSecondary'
                  disableRipple
                  sx={{ marginLeft: '8px' }}
                  onClick={handleFixConfirmation}
                >
                  <Typography
                    variant='h4'
                    sx={{
                      fontWeight: 700,
                    }}
                  >
                    {getLocalString(
                      'ConfirmationPage',
                      'buttons',
                      'updateDocument',
                    )}
                  </Typography>
                </Button>
              </Grid>
            )}
          </Grid>
          <StyledBlockWrapper
            container
            direction={'column'}
            sx={{
              padding: '16px 32px',
              marginTop: '20px',
            }}
          >
            <Box>
              {generalConfirmation.length > 0 &&
                generalConfirmation.map((control, index) => (
                  <Grid
                    key={index}
                    container
                    className={'pageLine'}
                    sx={{
                      marginTop: '4px',
                    }}
                  >
                    <Grid
                      sx={{
                        width: '200px',
                      }}
                    >
                      <Typography
                        variant='h4'
                        sx={{
                          color: '#41424E',
                          fontWeight: 700,
                        }}
                      >
                        {control.fieldName}
                      </Typography>
                    </Grid>
                    <Grid>
                      <Typography variant='h4'>
                        {Array.isArray(control.valueName) ? (
                          control.valueName.map((item, index) => (
                            <Box
                              key={index}
                              sx={{
                                color: '#212346',
                                marginTop: index > 0 ? '4px' : 0,
                              }}
                            >
                              {control.fieldId === 'documents' ? (
                                <StyledIssueLink
                                  target={'_blank'}
                                  to={handleGetRedirectUrl(
                                    control.valueId[index],
                                    control.fieldId,
                                  )}
                                >
                                  {item}
                                </StyledIssueLink>
                              ) : (
                                item
                              )}
                            </Box>
                          ))
                        ) : (
                          <Box
                            component={'span'}
                            sx={{
                              color: '#212346',
                              wordBreak: 'break-word',
                            }}
                          >
                            {control.fieldId === 'report' ? (
                              <StyledIssueLinkBox
                                component={'span'}
                                onClick={() =>
                                  handleReportLink(control.valueId)
                                }
                                sx={{
                                  color: '#3448FF',
                                  cursor: 'pointer',
                                }}
                              >
                                {control.valueName}
                              </StyledIssueLinkBox>
                            ) : control.fieldId === 'project' ? (
                              <StyledIssueLink
                                target={'_blank'}
                                to={handleGetRedirectUrl(
                                  control.valueId,
                                  control.fieldId,
                                )}
                              >
                                {control.valueName}
                              </StyledIssueLink>
                            ) : control.fieldId === 'status' ? (
                              <Box
                                sx={{
                                  color:
                                    control.valueId === '3' ||
                                    control.valueId === '2'
                                      ? '#E03737'
                                      : 'inherit',
                                  fontWeight:
                                    control.valueId === '3' ||
                                    control.valueId === '2'
                                      ? 600
                                      : 400,
                                }}
                              >
                                {control.valueName}
                              </Box>
                            ) : (
                              control.valueName
                            )}
                          </Box>
                        )}
                      </Typography>
                    </Grid>
                  </Grid>
                ))}
            </Box>
          </StyledBlockWrapper>
          {confirmationDocuments.columns && confirmationDocuments.rows && (
            <ConfirmationDocumentsTable
              confirmationDocuments={confirmationDocuments}
              pageState={confirmationPageState}
            ></ConfirmationDocumentsTable>
          )}
          {confirmationResult.columns.length > 0 &&
            confirmationResult.rows.length > 0 && (
              <>
                <Typography
                  variant='h3'
                  sx={{
                    marginTop: '28px',
                    fontWeight: 600,
                    color: '#082253',
                  }}
                >
                  {getLocalString('ConfirmationPage', 'labels', 'result')}
                </Typography>
                <ConfirmationResultTable
                  confirmationCanceled={confirmationCanceled}
                  confirmationResult={confirmationResult}
                ></ConfirmationResultTable>
              </>
            )}
        </Page>
      )}
    </>
  );
};
export default ConfirmationPage;
